<template lang="pug">
.follower-send-messages-tab
  .mb-3
  //- forms
  .contents.d-flex.flex-wrap.align-items-start
    .register-body
      //- title
      FormInput(v-model="title" name="title" key-name="タイトル" type="text" placeholder="タイトル"
        :maxlength="100" :validation-rules="{ required: true, max: 100 }" :styles="{ width: '720px', 'max-width': '720px' }" required)
      //- text
      FormTextarea(v-model="text" name="text" key-name="本文" placeholder="本文"
        :maxlength="1000" :validation-rules="{ required: true,  max: 1000 }" :styles="{ maxWidth: '720px', width: '720px', minHeight: '160px' }" required)
        template(#question)
          HelpIcon(popoverId="message-url-help" title="リンクの記載について")
            template(v-slot:text)
              span メッセージ内でURLリンクを使用したい場合は、改行してURLを記載することでリンクを送ることができます。
              br
              span 実際に送られるLINEメッセージは送信ボタン押下時のプレビューをご確認ください。
              br
              br
              strong 記載例
              br
              span 以下のリンクからコニカミノルタへアクセスしてください。
              br
              span https://www.konicaminolta.com
              br
              br
              strong リンク名を付ける方法
              br
              span 以下のようにリンク名とURLを記載すると、リンク名を表示することができます。
              br
              span [リンク名](URL)
              br
              br
              strong 記載例（リンク名を付ける方法）
              br
              span [HitomeQ](https://konicaminolta.com)

      //- files
      .register-row
        .register-group
          .register-key
            .left-side.d-flex
              span.key-name 添付ファイル
              span.comment ※最大9ファイルまで登録可能です
            .right-side
              HelpIcon(popoverId="temp-files-help" :title="'選択可能なファイルについて'")
                template(#text)
                  span 選択可能なファイルは、写真（jpeg）、動画（mp4もしくはmov）、pdfです。
                  br
                  span pdfはそのまま送るか、画像に変換して送るかをファイルごとに選択できます。
                  span 画像に変換した場合は、LINEですぐに見ることができます。
                  br
                  span 送信可能な動画のファイルサイズは200MB未満、pdfは50MB未満です。
                  br
                  span ファイルサイズが大きいと送信に時間がかかりますので、なるべく小さいサイズにしてください。
          .register-value
            .d-flex.flex-column
              MultiFileSelecter(ref="fileSelecter" :mimetypes="['video/quicktime', 'video/mp4', 'image/jpeg', 'application/pdf']"
                width="450px" height="74px" :limit="_fileLimit" :showPreview="true"
                :enablePreviewStatus="_enablePreviewStatus" @input="inputFiles" @preview="onFilePreview")
              label.need-convert-pdf
                input(v-model="needConvertPdf" name="need-convert-pdf" type="checkbox")
                span.text PDFファイルの場合、画像に変換する
              input(name="tempfiles-multiple-register-errors" data-vv-as="添付ファイル" style="display: none")
              .error-messages.text-break(v-for="error in errors.collect('tempfiles-multiple-register-errors')" :style="{ 'maxWidth': '500px' }")
                label.error-message {{ error }}
                br
      .register-row
        DraggableFiles(v-model="files" @delete="onFileDelete" :item-width="'468px'")

  //- buttons
  .box-footer.register-footer
    ValidationAlert(v-model="showValidationAlert")
    button.btn.preview-btn(type="button" @click="onMessagePreview") プレビュー
    .buttons.m-0
      button.btn.send-message-btn(type="button" @click="sendMessageConfirm") 送信

  //- modals
  SendMessageFilePreviewModal(ref="filePreviewModal")
  SendMessagePreviewModal(ref="messagePreviewModal" :files="files" @preview="onFilePreview" @submit="sendMessage")
</template>

<script lang="ts">
import { Component, Ref, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import SendMessageFormCommonService, { ExtDraggableFile } from "@/commons/services/SendMessageFormCommon";
import { SendMessageFunction } from "@/commons/models/Message";

import HelpIcon from "@/pc/basics/HelpIcon.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormTextarea from "@/pc/components/forms/FormTextarea.vue";
import MultiFileSelecter from "@/pc/basics/MultiFileSelecter.vue";
import DraggableFiles from "@/pc/components/DraggableFiles.vue";
import DraggableFile from "@/commons/models/DraggableFile";
import SendMessageFilePreviewModal from "@/pc/components/modals/SendMessageFilePreviewModal.vue";
import SendMessagePreviewModal from "@/pc/containers/followers/SendMessagePreviewModal.vue";

/**
 * ### 新規送信タブコンポーネント
 */
@Component({
  components: {
    HelpIcon,
    ValidationAlert,
    FormInput,
    FormTextarea,
    MultiFileSelecter,
    DraggableFiles,
    SendMessageFilePreviewModal,
    SendMessagePreviewModal
  },
  $_veeValidate: { validator: "new" }
})
export default class SendMessagesTab extends Vue {
  /** ファイルプレビューモーダル */
  @Ref() filePreviewModal!: SendMessageFilePreviewModal;
  /** メッセージ送信プレビューモーダル */
  @Ref() messagePreviewModal!: SendMessagePreviewModal;

  /** ページ全体ローディング表示可否 */
  isLoading: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** タイトル */
  title: string = "";
  /** 本文 */
  text: string = "";
  /** ファイル */
  files: Array<DraggableFile> = [];
  /** ファイル登録数 */
  readonly registerFileUploadLimit: number = 9;
  /** 添付ファイルのPDFを変換するか否か */
  needConvertPdf: boolean = true;

  /**
   * ### 登録可能なファイル数を返す
   */
  get _fileLimit(): number {
    const limit: number = this.registerFileUploadLimit - this.files.length;
    return limit;
  }

  /**
   * ### プレビューボタン表示可否
   */
  get _enablePreviewStatus(): boolean {
    return 0 < this.files.length ? true : false;
  }

  /** ### ファイルをリセットする */
  public resetFiles(): void {
    this.files = [];
    return;
  }

  /**
   * ### 画面のリフレッシュ
   */
  public async refresh(): Promise<void> {
    this.title = "";
    this.text = "";
    this.files = [];
    return;
  }

  /**
   * ### 子コンポーネントでファイルが選択された場合のイベント処理
   *
   * - ファイルが上限数以上なら無視する
   * - バリデーションを行う
   * - バリデーションエラーでなければ、ファイルを親コンポーネントに渡す
   */
  public async inputFiles(files: Array<File>): Promise<void> {
    this.isLoading = true;
    this.loadingMessage = "読込み中...";

    const sendMessageFormCommonService: SendMessageFormCommonService = new SendMessageFormCommonService(this.$validator, "tempfiles-multiple-register-errors");
    const addFiles: Array<ExtDraggableFile> = await sendMessageFormCommonService.createAddFile(files, this.registerFileUploadLimit, this.files.length, this.needConvertPdf);

    this.isLoading = false;

    if (0 < addFiles.length) {
      const files = [];
      for (const addFile of addFiles) {
        files.push(addFile.fileObject as File);
      }
      this.files = this.files.concat(files);

      for (let i = 0; i < this.files.length; i++) {
        this.files[i].key = i.toString();
      }
    }

    return;
  }

  /**
   * ### 添付ファイルプレビューボタン押下イベント処理
   *
   * - ダイアログで添付ファイルをプレビュー表示する
   */
  public onFilePreview(): void {
    this.filePreviewModal.show("", false, this.files);
    return;
  }

  /**
   * ### 指定されたファイルを削除する
   */
  public onFileDelete(index: number): void {
    this.files.splice(index, 1);
    return;
  }

  /**
   * ### メッセージ送信プレビュー
   */
  public async onMessagePreview(): Promise<void> {
    const functionContents: SendMessageFunction = this.createMessagePreviewContents();
    this.messagePreviewModal.show(functionContents, false);
    return;
  }

  /**
   * ### メッセージ送信プレビュー用のコンテンツを作成する
   */
  public createMessagePreviewContents(): SendMessageFunction {
    const functionContents: SendMessageFunction = {
      title: this.title,
      content: this.text,
      addReadButton: false, // フォロワーへのメッセージは読みましたボタン不要
      addOptionText: false, // フォロワーへのメッセージは 強調テキスト不要
      addReplyButton: false, // フォロワーへのメッセージは返信ボタン不要
      replyLimit: userStore.facility.messageSettings.replyLimit
    };

    return functionContents;
  }

  /**
   * メッセージ送信確認モーダルを表示する
   * 先にバリデーションチェックし、OKなら確認モーダルを表示する
   */
  public async sendMessageConfirm(): Promise<void> {
    this.isLoading = true;
    this.loadingMessage = "読込み中...";

    const validationResult: boolean = await this.$validator.validateAll();
    const fileValidationResult: boolean = this.validateFiles();
    if (validationResult && fileValidationResult) {
      this.showValidationAlert = false;
      const functionContents: SendMessageFunction = this.createMessagePreviewContents();
      this.messagePreviewModal.show(functionContents, true);
    } else {
      this.showValidationAlert = true;
    }

    this.isLoading = false;

    return;
  }

  /**
   * ### ファイルバリデーション
   *
   * - 何らかの原因でファイルが存在しないパターンがあったので、チェック処理を行う
   */
  public validateFiles(): boolean {
    for (const file of this.files) {
      if (!file || !file.name || file.size == 0) {
        this.$validator.errors.add({
          field: "tempfiles-multiple-register-errors",
          msg: `ファイルが破損等をしている可能性があり、ファイルを認識することができません。お手数ですが、再度選択し直してください。`
        });
        return false;
      }
    }
    return true;
  }

  /**
   * メッセージ送信確認ダイアログのOK処理
   */
  public async sendMessage(): Promise<void> {
    const messageModel: SendMessageModel = {
      title: this.title,
      text: this.text,
      files: this.files
    };
    this.$emit("sendMessage", messageModel);

    return;
  }
}

export interface SendMessageModel {
  /** メッセージタイトル */
  title: string;
  /** メッセージ本文 */
  text: string;
  /** ファイル */
  files: Array<File>;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

.follower-send-messages-tab {
  .send-message-btn {
    width: 104px;
  }

  .need-convert-pdf {
    margin: 8px 0 8px 0;

    // 画像でセレクトボックスを表現するので、input要素自体は非表示にする
    input[type="checkbox"] {
      display: none;

      // input要素の代わりに画像つきのspanにする
      // 選択しているか否かで画像を切り替える
      // for属性でcheckboxと紐づかせたspan要素のclickで、チェックボックスの状態が変更される
      & + span {
        // チェックボックス画像が全て表示されるように高さを指定する
        height: 46px;
        // チェックボックス画像が文字より大きく、文字が縦中央揃えに出来ないので、paddingで上下余白を取って位置調整
        padding: 6px 0 0 40px;
        background: url("../../../assets/login-checkbox-off.png") no-repeat left center;
        // チェックボックス画像のサイズを指定
        // 正方形なので要素の高さと同じになる
        background-size: 32px 32px;
      }
      &:checked + span {
        background: url("../../../assets/login-checkbox-on.png") no-repeat left center;
        // チェックボックス画像のサイズを指定
        // 正方形なので要素の高さと同じになる
        background-size: 32px 32px;
      }
    }
    .text {
      margin: 0 !important;
    }
  }

  .box-footer {
    padding: 10px 0 0 0 !important;
  }
}
</style>
